import type { Product } from 'TYPES/api.body';
import type {
  MadinaCart,
  MadinaCartItem,
  MadinaCategory,
  MadinaOrder,
  MadinaProduct,
  MadinaTracking
} from 'TYPES/madina';
import type { OrderDetailsResponse, OrderTrackingResponse } from 'TYPES/api.body';
import type { ServerCart } from 'components/molecules/Goods/utils';

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveCartItemUnitPrice = (item: MadinaCartItem): number => {
  const fromItem =
    item.unit_price ??
    item.unitPrice ??
    item.price ??
    item.amount ??
    item.product?.min_price ??
    item.product?.price;

  if (fromItem !== undefined && fromItem !== null) {
    return toNumber(fromItem);
  }

  const lineTotal = item.line_total ?? item.lineTotal ?? item.subtotal;
  if (lineTotal !== undefined && item.quantity > 0) {
    return toNumber(lineTotal) / item.quantity;
  }

  return 0;
};

const resolveCartItemLineTotal = (item: MadinaCartItem, unitPrice: number): number => {
  const lineTotal = item.line_total ?? item.lineTotal ?? item.subtotal;
  if (lineTotal !== undefined && lineTotal !== null) {
    return toNumber(lineTotal);
  }
  return unitPrice * (item.quantity ?? 1);
};

export const mapMadinaProductToLegacy = (product: MadinaProduct): Product => {
  const images = product.images?.length
    ? product.images
    : product.image_url
      ? [product.image_url]
      : [];

  const vendorName = product.vendor?.business_name || product.vendor?.name || 'Madina';
  const vendorId = product.vendor?.id ?? product.vendor_id ?? 0;

  return {
    productId: product.id,
    productName: product.name,
    name: product.name,
    price: product.min_price ?? product.price,
    quantityAvailable: product.available_qty ?? 0,
    minimumOrderQuantity: 1,
    bulkOrderPrice: product.price,
    barCode: product.sku ?? '',
    productImage1: images[0] ?? '',
    productImage2: images[1] ?? '',
    productImage3: images[2] ?? '',
    productImage4: images[3] ?? '',
    images: images as [],
    status: product.availability_status ?? 'in_stock',
    description: product.description ?? '',
    vendorName,
    vendorId,
    seller: {
      id: vendorId,
      businessName: vendorName,
      seller_logo: product.vendor?.logo ?? ''
    },
    category: product.category
      ? { id: product.category.id, name: product.category.name ?? '', logo: '' }
      : product.category_id
        ? { id: product.category_id, name: '', logo: '' }
        : undefined,
    subCategory: product.subcategory?.name ?? ''
  } as Product;
};

export const mapMadinaCategoryToLegacy = (category: MadinaCategory) => ({
  id: category.id,
  name: category.name,
  logo: category.image_url ?? category.logo ?? '',
  subCategories: (category.subcategories ?? []).map(sub => ({
    id: sub.id,
    name: sub.name,
    logo: sub.image_url ?? sub.logo ?? ''
  }))
});

export const mapMadinaCartToServerCart = (cart: MadinaCart): ServerCart => {
  const products = (cart.items ?? []).map(item => {
    const unitPrice = resolveCartItemUnitPrice(item);
    const lineTotal = resolveCartItemLineTotal(item, unitPrice);
    const product = item.product
      ? mapMadinaProductToLegacy({
          ...item.product,
          price: item.product.price || unitPrice,
          min_price: item.product.min_price ?? unitPrice
        })
      : mapMadinaProductToLegacy({
          id: item.product_id,
          name: `Product #${item.product_id}`,
          price: unitPrice,
          min_price: unitPrice,
          available_qty: item.available_qty
        });

    return {
      ...product,
      price: unitPrice || product.price,
      amount: unitPrice || product.price,
      quantity: item.quantity,
      totalAmount: lineTotal,
      cartItemId: item.cart_item_id ?? item.id,
      maxWeight: null,
      size: null
    };
  });

  const computedSubtotal = products.reduce((sum, p) => sum + toNumber(p.totalAmount ?? 0), 0);
  const totalAmount = toNumber(cart.total ?? cart.total_amount ?? cart.subtotal ?? cart.sub_total ?? computedSubtotal);
  const lineCount = products.length;

  return {
    id: cart.branch_id ?? 0,
    submitted: false,
    orders: [
      {
        id: cart.branch_id ?? 0,
        customer: { name: '' },
        orderRef: '',
        cartSize: lineCount,
        totalAmount,
        shippingFee: 0,
        orderStatus: 'OPEN',
        paymentStatus: 'PENDING',
        orderReturned: false,
        products,
        totalItems: lineCount,
        vendorId: 0,
        vendorEmail: '',
        vendorName: 'Madina',
        vendorPhone: '',
        vendorLogo: '',
        vendorAddress: null,
        vendorCategory: '',
        locationId: cart.branch_id
      }
    ]
  } as unknown as ServerCart;
};

const readOrderPlacedAt = (...values: Array<string | undefined | null>) => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (!text) continue;
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return text;
  }
  return '';
};

export const mapMadinaOrderToLegacy = (order: MadinaOrder): OrderDetailsResponse => {
  const products = (order.items ?? []).map(item => {
    const legacy = item.product
      ? mapMadinaProductToLegacy(item.product)
      : mapMadinaProductToLegacy({ id: item.product_id, name: `Product #${item.product_id}`, price: item.unit_price ?? 0 });

    return {
      ...legacy,
      quantity: item.quantity,
      totalAmount: item.line_total ?? legacy.price * item.quantity
    };
  });

  const orderStatus = order.status_label ?? order.status ?? 'PENDING';
  const totalItems = products.reduce((sum, product) => sum + (product.quantity ?? 0), 0) || products.length;

  return {
    id: order.id,
    orderRef: order.order_ref ?? order.orderRef ?? `MDN-${order.id}`,
    status: order.status ?? 'PENDING',
    orderStatus,
    paymentStatus: order.payment_status ?? 'PENDING',
    paymentType: order.payment_type ?? '',
    paymentMode: order.payment_type ?? '',
    paymentTrxRef: order.order_ref ?? '',
    totalAmount: order.total_amount ?? 0,
    shippingFee: order.shipping_fee ?? 0,
    products,
    carts: products,
    vendorName: 'Madina',
    vendorLogo: '',
    vendorCategory: '',
    deliveryMethod: order.delivery_method ?? 'delivery',
    deliveryStatus: orderStatus,
    orderDate: readOrderPlacedAt(order.order_date, order.created_at),
    totalItems,
    customer: { id: 0, name: '' }
  } as unknown as OrderDetailsResponse;
};

export const mapMadinaTrackingToLegacy = (
  tracking: MadinaTracking,
  orderId: string,
  order?: MadinaOrder | null
): OrderTrackingResponse => {
  const timeline = tracking.timeline ?? [];
  const status = tracking.status ?? order?.status ?? 'PENDING';
  const orderRef = tracking.order_ref ?? order?.order_ref ?? '';
  const orderDate = readOrderPlacedAt(tracking.order_date, order?.order_date, tracking.created_at, order?.created_at);

  return {
    orderId: tracking.order_id ?? order?.id ?? Number(orderId),
    orderRef,
    orderStatus: tracking.status_label ?? status,
    currentStatus: status,
    statusDate: orderDate,
    orderDate,
    trackingNumber: '',
    paymentStatus: tracking.payment_status ?? order?.payment_status ?? '',
    timeline
  } as OrderTrackingResponse & { timeline?: MadinaTracking['timeline'] };
};

export const isMadinaProductAvailable = (product: MadinaProduct | Product): boolean => {
  const tier1 = product as MadinaProduct & { can_add_to_cart?: boolean };
  if (tier1.can_add_to_cart === false) return false;
  if (tier1.availability_status === 'out_of_stock') return false;
  if ((tier1.available_qty ?? 1) <= 0 && tier1.availability_status) return false;
  return true;
};
